// src/pages/screens/DatabaseListScreen.js
import { useEffect, useState } from "react";
import { useDatabase } from "../databaseContext";
import { useNavigate } from "react-router-dom";
import { Select, message, Button, Modal, Input, Table } from "antd";
import { UserOutlined, TableOutlined, SafetyOutlined } from "@ant-design/icons";
import { Eye, KeyRound, Search, ShieldCheck, Table2 } from "lucide-react";
import { format } from "date-fns"; // Or your own formatting
import axios from "axios";
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";
import { AN_COLORS } from "../../../design-system/tokens";
import {
  EmbeddedShell,
  FilterCard,
  FilterField,
  MonoCell,
  RowActionMenu,
  SquareBadge,
  StatusPill,
  TableCard,
  anTableProps,
  fmtDate,
  tableEmpty,
} from "../../../design-system/tableUi";

const DatabaseListScreen = ({ showNotification, onTabChange, onDatabaseSelect }) => {
  const navigate = useNavigate();
  const { dbNames, fetchDbNames, dbList, setDbList, fetchDbList } = useDatabase();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  // Mirrors the Host input so typing stays responsive while the fetch is debounced.
  const [hostInput, setHostInput] = useState("");
  const [hostSearchTimeout, setHostSearchTimeout] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  // For user messages
  const [notification, contextHolder] = message.useMessage();

  // Fetch unique database names on mount
  useEffect(() => {
    fetchDbNames();
  }, [fetchDbNames]);

  // Handle host filter from VMScreen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hostFilter = params.get("host");
    if (hostFilter) {
      setSelectedHost(hostFilter);
      setHostInput(hostFilter);
    }
  }, []);

  // Actually fetch the DB list whenever filters/page change
  useEffect(() => {
    loadDatabaseData();
  }, [selectedDatabase, selectedStatus, selectedHost, currentPage, pageSize]);

  const loadDatabaseData = async () => {
    setIsLoading(true);
    const filters = [];
    if (selectedDatabase) {
      filters.push({ filterParameter: "Database", filterValue: selectedDatabase });
    }
    if (selectedStatus) {
      filters.push({ filterParameter: "Status", filterValue: selectedStatus });
    }
    if (selectedHost) {
      filters.push({ filterParameter: "Host", filterValue: selectedHost });
    }

    try {
      const { data, totalCount: tc } = await fetchDbList(filters, pageSize, currentPage + 1);
      setDbList(data);
      setTotalCount(tc);
    } catch (error) {
      notification.error({ content: "Failed to fetch database list" });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch tables for a database
  const fetchTableNames = async (dbName, host) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        dbName: dbName,
        host: host,
      };

      const res = await axios.post(`${REACT_APP_API}/api/v1/databaseService/listTables`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      });

      if (res.data?.data) {
        // Convert the response data into an array of table names
        return Object.keys(res.data.data);
      }
      return [];
    } catch (error) {
      notification.error({ message: "Error fetching table names", description: error.message });
      return [];
    }
  };

  // Function to fetch table fields
  const fetchTableFields = async (dbName, tables) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        tenantName: dbName,
        tableNames: tables,
      };

      const res = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getTableFields`,
        payload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );

      if (res.data?.fields) {
        return res.data.fields;
      }
      return {};
    } catch (error) {
      notification.error({ message: "Error fetching table fields", description: error.message });
      return {};
    }
  };

  const handleMenuClick = async (db, action) => {
    switch (action) {
      case "viewUsers":
        // Example logic
        navigate("/pam/databases/users", { state: { databaseFilter: db.db_name } });
        break;
      case "createPolicy":
        notification.loading({ content: "Preparing policy creation...", key: "policyCreation" });

        // Generate a dynamic policy name
        const prefix = "db-";
        const characters = "0123456789";
        let policyName = prefix;
        const sections = [7, 8];

        for (let sectionLength of sections) {
          for (let i = 0; i < sectionLength; i++) {
            const randomCharIndex = Math.floor(Math.random() * characters.length);
            policyName += characters.charAt(randomCharIndex);
          }
          if (sectionLength !== 8) {
            policyName += "-";
          }
        }

        // Fetch tables for this database
        const tables = await fetchTableNames(db.db_name, db.host);

        // Create initial field masking object (empty arrays)
        const initialFieldMasking = {};
        tables.forEach((table) => {
          initialFieldMasking[table] = [];
        });

        // Get a subset of tables (up to 5) to preselect by default
        const preselectedTables = [];

        // Fetch fields for the selected tables
        const fields = await fetchTableFields(db.db_name, preselectedTables);

        // Create the policy draft
        const policyDraft = {
          policyType: "database",
          policyName: policyName,
          policyJson: {
            policyName: policyName,
            policyType: "database",
            database: {
              database_name: db.db_name,
              host: db.host,
              tables: preselectedTables,
              field_masking: initialFieldMasking,
            },
          },
          // Store the fields data to be used when the form loads
          fieldsData: fields,
        };

        // Save to localStorage
        localStorage.setItem("policyDraft.basicPolicyInformation", JSON.stringify(policyDraft));

        console.log("Policy draft saved:", policyDraft);
        notification.success({ content: "Ready to create policy", key: "policyCreation" });
        // The shared policy editor picks up the database (and every table).
        navigate("/pam/policies/database/new", {
          state: { databasePrefill: { host: db.host, database: db.db_name, databaseType: db.db_type, tables: preselectedTables } },
        });
        break;
      case "viewTables":
        onDatabaseSelect(db.db_name);
        onTabChange("tables", { host: db.host, database: db.db_name });
        break;
      case "viewDetails":
        Modal.info({
          title: "Database Details",
          width: 700,
          className: "database-details-modal",
          okText: "Close",
          content: (
            <div className="space-y-6 p-4">
              <div className="rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Database Name</p>
                        <p className="font-medium mt-1">{db.db_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Type</p>
                        <p className="font-medium mt-1">{db.db_type}</p>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Host</p>
                        <p className="font-medium mt-1 truncate" title={db.host}>
                          {db.host}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Port</p>
                        <p className="font-medium mt-1">{db.port}</p>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Status</p>
                        <div className="mt-1">
                          <StatusPill status={db.status} />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Created At</p>
                        <p className="font-medium mt-1">
                          {format(new Date(db.created_at), "yyyy-MM-dd HH:mm:ss")}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-base mb-4">Quick Actions</h4>
                <div className="flex gap-3">
                  <Button
                    icon={<UserOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "viewUsers");
                    }}
                  >
                    View Users
                  </Button>
                  <Button
                    icon={<SafetyOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "createPolicy");
                    }}
                  >
                    Grant Access
                  </Button>
                  <Button
                    icon={<TableOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "viewTables");
                    }}
                  >
                    View Tables
                  </Button>
                </div>
              </div>
            </div>
          ),
        });
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Database",
      dataIndex: "db_name",
      key: "db_name",
      render: (text, record) => (
        <button
          type="button"
          onClick={() => handleMenuClick(record, "viewDetails")}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            fontWeight: 500,
            fontSize: 13,
            color: AN_COLORS.primary,
            cursor: "pointer",
          }}
        >
          {text || "—"}
        </button>
      ),
    },
    {
      title: "Type",
      dataIndex: "db_type",
      key: "db_type",
      width: 130,
      render: (text) => <SquareBadge>{text}</SquareBadge>,
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Port",
      dataIndex: "port",
      key: "port",
      width: 90,
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (text) => <StatusPill status={text} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 190,
      render: (text) => <MonoCell>{fmtDate(text)}</MonoCell>,
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <RowActionMenu
          items={[
            {
              key: "viewDetails",
              icon: <Eye size={13} />,
              label: "View details",
              onClick: () => handleMenuClick(record, "viewDetails"),
            },
            {
              key: "viewUsers",
              icon: <KeyRound size={13} />,
              label: "View users",
              onClick: () => handleMenuClick(record, "viewUsers"),
            },
            {
              key: "viewTables",
              icon: <Table2 size={13} />,
              label: "View tables",
              onClick: () => handleMenuClick(record, "viewTables"),
            },
            { type: "divider" },
            {
              key: "createPolicy",
              icon: <ShieldCheck size={13} />,
              label: "Grant access",
              onClick: () => handleMenuClick(record, "createPolicy"),
            },
          ]}
        />
      ),
    },
  ];

  const resetPage = () => setCurrentPage(0);

  const appliedFilters = [
    selectedDatabase && {
      key: "Database",
      label: `Database · ${selectedDatabase}`,
      clear: () => {
        setSelectedDatabase(null);
        resetPage();
      },
    },
    selectedStatus && {
      key: "Status",
      label: `Status · ${selectedStatus}`,
      clear: () => {
        setSelectedStatus(null);
        resetPage();
      },
    },
    selectedHost && {
      key: "Host",
      label: `Host · ${selectedHost}`,
      clear: () => {
        setSelectedHost(null);
        setHostInput("");
        resetPage();
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedDatabase(null);
    setSelectedStatus(null);
    setSelectedHost(null);
    setHostInput("");
    resetPage();
  };

  return (
    <EmbeddedShell>
      {contextHolder}

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={dbList.length}
        total={totalCount}
      >
        <FilterField label="Host">
          <Input
            allowClear
            placeholder="Search by host (e.g. 52.142)"
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            value={hostInput}
            style={{ height: 36 }}
            onChange={(e) => {
              const value = e.target.value;
              setHostInput(value);
              if (hostSearchTimeout) clearTimeout(hostSearchTimeout);
              setHostSearchTimeout(
                setTimeout(() => {
                  setSelectedHost(value || null);
                  setCurrentPage(0);
                }, 500)
              );
            }}
          />
        </FilterField>

        <FilterField label="Database">
          <Select
            showSearch
            allowClear
            placeholder="Any database"
            style={{ width: "100%", height: 36 }}
            value={selectedDatabase}
            onChange={(value) => {
              setSelectedDatabase(value ?? null);
              setCurrentPage(0);
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            // Names arrive as "dbname (host)" — show/filter on the name only.
            options={[...new Set(dbNames.map((db) => db.split(" (")[0]))].map((name) => ({
              label: name,
              value: name,
            }))}
          />
        </FilterField>

        <FilterField label="Status">
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: "100%", height: 36 }}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value ?? null);
              setCurrentPage(0);
            }}
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={dbList}
          loading={isLoading}
          rowKey={(record) => record.id}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0
              ? "No databases match your filters."
              : "No databases found yet — install a database agent to get started."
          )}
          pagination={{
            current: currentPage + 1,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setCurrentPage(page - 1),
          }}
        />
      </TableCard>
    </EmbeddedShell>
  );
};

export default DatabaseListScreen;
